/** A sandbox's own SMS number (DSEC D5; plan §6 "Ops: the same state + Remove").
 *
 *  Its developers set it up in the sandbox's dashboard (Developer → Overview → Messaging): one number and
 *  a Conversations service in the platform Twilio account. Ops see the state with its Twilio SIDs and can
 *  remove it: the number is released, the service deleted with the sandbox's chat history, and the
 *  sandbox's SMS / WhatsApp are captured again. Refused while the sandbox is suspended (resume it first). */
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { sandboxApi, sandboxErrorMessage, type SandboxSms } from '@/api/sandbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';

import { When } from './When';

const STATE_LABEL: Record<string, { label: string; color: 'default' | 'success' | 'warning' }> = {
  off: { label: 'Off', color: 'default' },
  setting_up: { label: 'Setting up (unfinished)', color: 'warning' },
  on: { label: 'On', color: 'success' },
  removing: { label: 'Removing (unfinished)', color: 'warning' },
};

function Sid({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <Typography variant="body2" color="text.secondary">
      {label}:{' '}
      <ValueActions actions={[act.copy(value, `Copy the ${label}`)]}>
        <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
          {value}
        </Box>
      </ValueActions>
    </Typography>
  );
}

export function SmsSection({
  topId,
  code,
  sms,
  suspended,
  canManage,
}: {
  topId: number;
  code: string;
  sms: SandboxSms | null | undefined;
  suspended: boolean;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () => sandboxApi.removeSms(topId),
    onSuccess: () => {
      snackbar.show(`${code}’s SMS number was removed.`, 'success');
      void qc.invalidateQueries({ queryKey: ['tourOperators', 'sandbox', topId] });
    },
    onError: (e) => {
      snackbar.show(sandboxErrorMessage(e, 'Remove failed.'), 'error');
      void qc.invalidateQueries({ queryKey: ['tourOperators', 'sandbox', topId] });
    },
  });
  const state = sms?.state ?? 'off';
  const label = STATE_LABEL[state] ?? { label: state, color: 'default' as const };
  const removable =
    state !== 'off' || Boolean(sms?.number_sid || sms?.service_sid || sms?.pending_number);
  const blocked = suspended ? 'Resume the sandbox first.' : null;
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" variant="outlined" color={label.color} label={label.label} />
        {sms?.number && (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {sms.number}
          </Typography>
        )}
        {sms?.at && (
          <Typography variant="body2" color="text.secondary" component="div">
            since <When iso={sms.at} />
            {sms.by ? ` by ${sms.by}` : ''}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        {state === 'off'
          ? 'No number of its own: every SMS and WhatsApp message of the sandbox is captured in its outbox. Its developers set one up from the sandbox’s dashboard.'
          : 'One number and a Conversations service in the platform Twilio account. Its texts still go through the outbound mode and allowlist.'}
      </Typography>
      <Sid label="number SID" value={sms?.number_sid} />
      <Sid label="service SID" value={sms?.service_sid} />
      <Sid label="number being bought" value={sms?.pending_number} />
      {sms?.error && <Alert severity="warning">Last attempt stopped: {sms.error}</Alert>}
      {sms?.us_registration_note && <Alert severity="info">{sms.us_registration_note}</Alert>}
      {canManage && removable && (
        <Box>
          <Button
            color="error"
            variant="outlined"
            disabled={Boolean(blocked) || remove.isPending}
            onClick={() => setConfirm(true)}
          >
            Remove SMS…
          </Button>
          {blocked && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              {blocked}
            </Typography>
          )}
        </Box>
      )}
      <ConfirmDialog
        open={confirm}
        title={`Remove ${code}’s SMS number`}
        message={`${sms?.number ? `${sms.number} is released to Twilio and cannot be got back. ` : ''}The sandbox’s Conversations service is deleted with its chat history, and its SMS and WhatsApp messages are captured again.`}
        confirmLabel="Remove SMS"
        destructive
        onConfirm={() => {
          setConfirm(false);
          remove.mutate();
        }}
        onCancel={() => setConfirm(false)}
      />
    </Stack>
  );
}
